/**
 * Render an RRULE string as human-readable text. Display-time only — distinct
 * from `lib/recurrence.ts`, which is the form-side preset/serialise/preview
 * helper. Different consumers, different concerns.
 *
 * Falls back to the raw rule on unknown FREQ values, matching the prior
 * inline implementation that lived in PublicEvent.tsx.
 */

const DAYS: Record<string, string> = {
  MO: "Monday",
  TU: "Tuesday",
  WE: "Wednesday",
  TH: "Thursday",
  FR: "Friday",
  SA: "Saturday",
  SU: "Sunday",
};

export function humanizeRrule(rule: string): string {
  const parts: Record<string, string> = {};
  for (const seg of rule.split(";")) {
    const eq = seg.indexOf("=");
    if (eq > 0) parts[seg.slice(0, eq)!] = seg.slice(eq + 1);
  }
  const interval = parseInt(parts["INTERVAL"] ?? "1", 10);
  const freq = parts["FREQ"];
  const byday = parts["BYDAY"];
  const bymonthday = parts["BYMONTHDAY"];

  if (freq === "DAILY") return interval === 1 ? "Every day" : `Every ${interval} days`;

  if (freq === "WEEKLY") {
    if (!byday) return interval === 1 ? "Every week" : `Every ${interval} weeks`;
    const days = byday.split(",").map((d) => DAYS[d] ?? d);
    const dayStr =
      days.length === 1
        ? days[0]!
        : days.slice(0, -1).join(", ") + " and " + days[days.length - 1];
    return interval === 1 ? `Every ${dayStr}` : `Every ${interval} weeks on ${dayStr}`;
  }

  if (freq === "MONTHLY") {
    if (!bymonthday) return interval === 1 ? "Monthly" : `Every ${interval} months`;
    const n = parseInt(bymonthday, 10);
    const sfx = n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";
    return interval === 1
      ? `Monthly on the ${n}${sfx}`
      : `Every ${interval} months on the ${n}${sfx}`;
  }

  if (freq === "YEARLY") return interval === 1 ? "Yearly" : `Every ${interval} years`;
  return rule;
}
